import { PLACEHOLDER_IMAGE_VIDEO } from '@presentation/constants';
import type { FC } from 'react';
import { Suspense, lazy, useCallback, useMemo, useRef } from 'react';
import { FaAngleLeft, FaAngleRight } from 'react-icons/fa';
import * as Styled from './styled';
import type { SlideshowProps } from './types';

export * from './types';

const ReactPlayer = lazy(() => import('react-player/youtube'));

export const Slideshow: FC<SlideshowProps> = (props) => {
  const {
    items,
    index = 0,
    onImageClick,
    onThumbnailClick,
    onPrevious,
    onNext,
    aspectRatio,
    ...rest
  } = props;

  const hasMultiple = useMemo(() => (items ?? []).length > 1, [items]);

  const imageContainerRef = useRef<HTMLDivElement>(null);

  const scrollToChild = useCallback((childIndex: number) => {
    if (!imageContainerRef.current) {
      throw new Error('imageContainerRef is not found');
    }
    const element = imageContainerRef.current.children?.item?.(childIndex) as any;
    if (!element) {
      throw new Error('Child element is not found');
    }
    const scrollLeft = element.offsetLeft;
    imageContainerRef.current.scrollTo({
      left: scrollLeft,
      behavior: 'smooth',
    });
  }, []);

  const currentItem = useMemo(() => {
    return items[index];
  }, [index, items]);

  return (
    <Styled.Container {...rest}>
      <Styled.MainImageWrapper>
        {hasMultiple && !currentItem.isVideo && (
          <>
            <Styled.ArrowContainer
              onClick={() => {
                const nextIndex = (index + items.length - 1) % items.length;
                scrollToChild(nextIndex);
                onPrevious?.();
              }}
            >
              <FaAngleLeft />
            </Styled.ArrowContainer>
            <Styled.ArrowContainer
              right
              onClick={() => {
                const nextIndex = (index + 1) % items.length;
                scrollToChild(nextIndex);
                onNext?.();
              }}
            >
              <FaAngleRight />
            </Styled.ArrowContainer>
          </>
        )}
        <Styled.MainImage
          aspectRatio={aspectRatio}
          src={currentItem.isVideo ?? false ? undefined : currentItem.src}
          role={currentItem.isVideo ?? false ? undefined : 'img'}
          alt={currentItem.alt}
          onClick={() => onImageClick?.()}
          onContextMenu={(e) => e.preventDefault()}
        >
          <Suspense fallback="...">
            <ReactPlayer
              url={currentItem.src}
              className="react-player"
              width="100%"
              height="100%"
              controls
            />
          </Suspense>
        </Styled.MainImage>
      </Styled.MainImageWrapper>
      {hasMultiple && (
        <Styled.ThumbnailsContainer ref={imageContainerRef}>
          {items.map((image, imageIndex) => (
            <Styled.Thumbnail
              active={index === imageIndex}
              key={imageIndex}
              src={
                image.isVideo
                  ? image.thumbnail ?? PLACEHOLDER_IMAGE_VIDEO
                  : image.thumbnail ?? image.src
              }
              onClick={() => {
                scrollToChild(imageIndex);
                onThumbnailClick?.(imageIndex);
              }}
              onContextMenu={(e) => e.preventDefault()}
            />
          ))}
        </Styled.ThumbnailsContainer>
      )}
    </Styled.Container>
  );
};
